import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { listApplicationDrafts, deleteApplicationDraft } from '../../../services/loanService';
import type { ApplicationDraft } from '../../../services/schemas/loanServiceSchemas/applicationDraft';
import { DRAFT_QUERY_PARAM } from '../../loan-application/useWizardDraftPersistence';

/** The wizard's last step is Review; a draft's progress is how far along the steps it got. */
const LAST_STEP = 7;

const formatSaved = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    return `${date.toLocaleDateString('en-ZA')} ${date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
};

const isToday = (value?: string): boolean => {
    if (!value) return false;
    return new Date(value).toDateString() === new Date().toDateString();
};

/**
 * Loan applications still being captured. The wizard saves them as they are typed; anyone on the team can
 * continue one from here, or discard it.
 */
const PendingSubmissions = () => {
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.auth.user);
    const myName = user?.name || user?.username || '';
    const [drafts, setDrafts] = useState<ApplicationDraft[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [onlyMine, setOnlyMine] = useState(false);
    const [search, setSearch] = useState('');
    const [toDiscard, setToDiscard] = useState<ApplicationDraft | null>(null);
    const [isDiscarding, setIsDiscarding] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const response = await listApplicationDrafts();
            setDrafts(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load application drafts:', error);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const shown = useMemo(() => {
        const term = search.trim().toLowerCase();
        return drafts.filter(d =>
            (!onlyMine || (myName && (d.startedByName === myName || d.savedByName === myName)))
            && (!term || (d.customerName || '').toLowerCase().includes(term)
                || (d.startedByName || '').toLowerCase().includes(term)));
    }, [drafts, onlyMine, myName, search]);

    const stats = [
        { title: 'Drafts', value: drafts.length, icon: 'fa-file', color: 'primary' },
        { title: 'Business', value: drafts.filter(d => d.customerType === 'business').length, icon: 'fa-building', color: 'secondary' },
        { title: 'Individual', value: drafts.filter(d => d.customerType === 'individual').length, icon: 'fa-user', color: 'info' },
        { title: 'Saved today', value: drafts.filter(d => isToday(d.modifiedDate)).length, icon: 'fa-clock-o', color: 'warning' },
    ];

    const discard = async () => {
        if (!toDiscard?.id) return;
        setIsDiscarding(true);
        try {
            await deleteApplicationDraft(toDiscard.id);
            toast.success('Draft discarded');
            setToDiscard(null);
            load();
        } catch (error: any) {
            toast.error(error?.message || 'Could not discard the draft');
        } finally {
            setIsDiscarding(false);
        }
    };

    const columns = [
        {
            name: 'Customer',
            selector: (row: ApplicationDraft) => row.customerName || '',
            sortable: true,
            grow: 2,
            cell: (row: ApplicationDraft) => (
                <span className={row.customerName ? 'fw-semibold' : 'text-muted fst-italic'}>
                    {row.customerName || 'Not captured yet'}
                </span>
            ),
        },
        {
            name: 'Type',
            selector: (row: ApplicationDraft) => row.customerType || '',
            sortable: true,
            cell: (row: ApplicationDraft) => row.customerType ? (
                <span className={`badge bg-${row.customerType === 'individual' ? 'info' : 'primary'}`}>
                    {row.customerType === 'individual' ? 'Individual' : 'Business'}
                </span>
            ) : <span className="text-muted">-</span>,
        },
        {
            name: 'Progress',
            selector: (row: ApplicationDraft) => row.currentStep || 0,
            sortable: true,
            grow: 2,
            cell: (row: ApplicationDraft) => (
                <div className="w-100">
                    <div className="small mb-1">{row.stepLabel || `Step ${(row.currentStep || 0) + 1}`}</div>
                    <div className="progress" style={{ height: '6px' }}>
                        <div className="progress-bar bg-primary" role="progressbar"
                            style={{ width: `${Math.round(((row.currentStep || 0) / LAST_STEP) * 100)}%` }}></div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Started by',
            selector: (row: ApplicationDraft) => row.startedByName || '',
            sortable: true,
            cell: (row: ApplicationDraft) => (
                <div className="small">
                    <div>{row.startedByName || '-'}</div>
                    {row.savedByName && row.savedByName !== row.startedByName && (
                        <div className="text-muted">last saved by {row.savedByName}</div>
                    )}
                </div>
            ),
        },
        {
            name: 'Last saved',
            selector: (row: ApplicationDraft) => row.modifiedDate || '',
            sortable: true,
            cell: (row: ApplicationDraft) => <span className="small">{formatSaved(row.modifiedDate)}</span>,
        },
        {
            name: 'Actions',
            right: true,
            cell: (row: ApplicationDraft) => (
                <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/loan-application/new?${DRAFT_QUERY_PARAM}=${row.id}`)}>
                        <i className="fa fa-pencil me-1"></i>Continue
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setToDiscard(row)} title="Discard this draft">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Pending Submissions</li>
                            </ol>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-primary" onClick={() => navigate('/loan-application/new')}>
                                <i className="fa fa-plus me-2"></i>New application
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 row-deck">
                        {stats.map((stat) => (
                            <div key={stat.title} className="col-lg-3 col-md-6 col-sm-6">
                                <div className="card">
                                    <div className="card-body">
                                        <i className={`fa ${stat.icon} position-absolute top-0 end-0 mt-4 me-3 text-${stat.color}`} style={{ fontSize: '24px' }}></i>
                                        <div className="mb-2 text-uppercase">{stat.title}</div>
                                        <div><span className="h4">{stat.value}</span></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div>
                                        <h6 className="card-title mb-0">Applications still being captured</h6>
                                        <small className="text-muted">Saved automatically while they are typed. Anyone on the team can continue one.</small>
                                    </div>
                                    <div className="d-flex gap-2 align-items-center">
                                        <input type="text" className="form-control form-control-sm" style={{ width: 220 }}
                                            placeholder="Search customer or person" value={search} onChange={(e) => setSearch(e.target.value)} />
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button type="button" className={`btn ${!onlyMine ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setOnlyMine(false)}>All</button>
                                            <button type="button" className={`btn ${onlyMine ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setOnlyMine(true)}>Mine</button>
                                        </div>
                                        <button className="btn btn-sm btn-light" onClick={load} disabled={isLoading} title="Refresh">
                                            <i className={`fa fa-refresh ${isLoading ? 'fa-spin' : ''}`}></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {isError ? (
                                        <p className="text-muted text-center py-4 mb-0">
                                            <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                                            Could not load the drafts.
                                        </p>
                                    ) : (
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={shown}
                                                progressPending={isLoading}
                                                pagination
                                                highlightOnHover
                                                striped
                                                responsive
                                                noDataComponent={<div className="text-muted py-4">No applications are being captured right now.</div>}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={!!toDiscard} onHide={() => setToDiscard(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Discard draft</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Discard the unfinished application for <strong>{toDiscard?.customerName || 'this customer'}</strong>?
                    Everything captured in it will be deleted.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setToDiscard(null)}>Keep it</Button>
                    <Button variant="danger" onClick={discard} disabled={isDiscarding}>
                        {isDiscarding ? 'Discarding...' : 'Discard'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PendingSubmissions;
